import React, { useMemo, useState } from 'react';

import { Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { usePermission } from '@admin/hooks/use_permission';
import { ACCESS_CONTROL_PERMISSIONS_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  AccessControlPermissionsAdminQuery,
  AccessControlRoleAdmin,
  Status,
  useAccessControlRolePermissionsUpdateMutation,
} from '@admin/schema';

import {
  ActionTitle,
  Actions,
  Badge,
  EmptyState,
  ExplorerPermissionItem,
  ExplorerPermissionList,
  ExplorerSidebar,
  Header,
  Layout,
  PermissionCard,
  PermissionDescription,
  PermissionGrid,
  PermissionGroup,
  PermissionGroupHeader,
  ScrollableContent,
  SearchInput,
  SelectionMeta,
  SelectionTitle,
  Subtitle,
  Title,
  Toolbar,
  formatRoleName,
  groupPermissions,
  matchesQuery,
  sortPermissions,
} from './permissions_admin_shared';
import { roleGrantCountByPermissionId } from './permissions_admin_utils';

type SelectedRole = Pick<AccessControlRoleAdmin, 'id' | 'name' | 'description' | 'permissionIds'>;

type AccessControlPermissionsAdminRolesProps = {
  data: AccessControlPermissionsAdminQuery;
  onSave: () => void;
};

export const AccessControlPermissionsAdminRoles: React.FC<AccessControlPermissionsAdminRolesProps> = ({
  data,
  onSave,
}) => {
  const admin = data.accessControlPermissionsAdmin;
  const roles = admin.roles;
  const permissions = admin.permissions;

  const canUpdate = usePermission(
    ACCESS_CONTROL_PERMISSIONS_ADMIN.resource,
    ACCESS_CONTROL_PERMISSIONS_ADMIN.actions.update,
  );
  const [roleSearch, setRoleSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>(null);
  const [draftPermissionIds, setDraftPermissionIds] = useState<string[]>([]);
  const [expandedResources, setExpandedResources] = useState<Record<string, boolean>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const [execute, { loading }] = useAccessControlRolePermissionsUpdateMutation({ client });

  const roleGrantCounts = useMemo(() => roleGrantCountByPermissionId(roles), [roles]);

  const filteredRoles = useMemo(
    () =>
      roles.filter((role) => matchesQuery(roleSearch, [role.name, formatRoleName(role.name), role.description ?? ''])),
    [roles, roleSearch],
  );

  const permissionGroups = useMemo(() => {
    const grouped = groupPermissions(permissions);
    return Object.entries(grouped)
      .filter(([resource]) => matchesQuery(resourceSearch, [resource]))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([resource, resourcePermissions]) => [resource, sortPermissions(resourcePermissions)] as const);
  }, [permissions, resourceSearch]);

  const onSelect = (role: SelectedRole) => {
    setSelectedRole(role);
    setDraftPermissionIds(role.permissionIds);
    setSaveError(null);
  };

  const togglePermission = (permissionId: string) => {
    setDraftPermissionIds((current) =>
      current.includes(permissionId) ? current.filter((id) => id !== permissionId) : [...current, permissionId],
    );
  };

  const toggleResource = (resource: string) => {
    setExpandedResources((current) => ({ ...current, [resource]: !current[resource] }));
  };

  const expandAll = () => {
    setExpandedResources(Object.fromEntries(permissionGroups.map(([resource]) => [resource, true])));
  };

  const collapseAll = () => {
    setExpandedResources(Object.fromEntries(permissionGroups.map(([resource]) => [resource, false])));
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;

    setSaveError(null);
    try {
      const { data: mutationData } = await execute({
        variables: { roleId: selectedRole.id, permissionIds: draftPermissionIds },
      });
      const result = mutationData?.accessControlRolePermissionsUpdate;
      if (result?.status !== Status.Ok) {
        setSaveError(result?.error ?? 'Failed to save role permissions.');
        return;
      }

      if (result.role?.permissionIds) {
        setDraftPermissionIds(result.role.permissionIds);
      }
      onSave();
    } catch (mutationError) {
      setSaveError(mutationError instanceof Error ? mutationError.message : 'Failed to save role permissions.');
    }
  };

  return (
    <>
      <Toolbar>
        <SearchInput
          aria-label="Search roles"
          placeholder="Search roles"
          value={roleSearch}
          onChange={(event) => setRoleSearch(event.target.value)}
        />
        <SearchInput
          aria-label="Filter resources"
          placeholder="Filter resources"
          value={resourceSearch}
          onChange={(event) => setResourceSearch(event.target.value)}
        />
        <button type="button" className="btn btn-default btn-sm" onClick={expandAll}>
          Expand all
        </button>
        <button type="button" className="btn btn-default btn-sm" onClick={collapseAll}>
          Collapse all
        </button>
      </Toolbar>

      <Layout>
        <ExplorerSidebar>
          {filteredRoles.length === 0 ? (
            <EmptyState>No roles match your search.</EmptyState>
          ) : (
            <ExplorerPermissionList>
              {filteredRoles.map((role) => (
                <ExplorerPermissionItem
                  key={role.id}
                  type="button"
                  active={role.id === selectedRole?.id}
                  onClick={() => onSelect(role)}
                >
                  <SelectionTitle>{formatRoleName(role.name)}</SelectionTitle>
                  <SelectionMeta>
                    {role.permissionIds.length} permissions · {role.assignedUserCount} explicit users
                  </SelectionMeta>
                </ExplorerPermissionItem>
              ))}
            </ExplorerPermissionList>
          )}
        </ExplorerSidebar>

        <Panel>
          <Panel.Body>
            {selectedRole ? (
              <>
                <Header>
                  <Title style={{ fontSize: 22 }}>{formatRoleName(selectedRole.name)}</Title>
                  <Subtitle>{selectedRole.description || 'Toggle which permissions this role grants.'}</Subtitle>
                </Header>

                <ScrollableContent>
                  {permissionGroups.length === 0 ? (
                    <EmptyState>No resources match your filter.</EmptyState>
                  ) : (
                    permissionGroups.map(([resource, resourcePermissions]) => (
                      <PermissionGroup key={resource}>
                        <PermissionGroupHeader
                          type="button"
                          aria-expanded={Boolean(expandedResources[resource])}
                          onClick={() => toggleResource(resource)}
                        >
                          <span>{resource}</span>
                          <span>{expandedResources[resource] ? '−' : '+'}</span>
                        </PermissionGroupHeader>
                        {expandedResources[resource] && (
                          <PermissionGrid>
                            {resourcePermissions.map((permission) => (
                              <PermissionCard key={permission.id}>
                                <input
                                  type="checkbox"
                                  checked={draftPermissionIds.includes(permission.id)}
                                  disabled={!canUpdate}
                                  onChange={() => togglePermission(permission.id)}
                                />
                                <span>
                                  <ActionTitle>{permission.action}</ActionTitle>
                                  <PermissionDescription>
                                    {permission.description || permission.key}
                                  </PermissionDescription>
                                  <Badge tone={(roleGrantCounts.get(permission.id) ?? 0) === 0 ? 'warning' : 'neutral'}>
                                    {(roleGrantCounts.get(permission.id) ?? 0) === 1
                                      ? '1 role grants this'
                                      : `${roleGrantCounts.get(permission.id) ?? 0} roles grant this`}
                                  </Badge>
                                </span>
                              </PermissionCard>
                            ))}
                          </PermissionGrid>
                        )}
                      </PermissionGroup>
                    ))
                  )}
                </ScrollableContent>

                {saveError && <Text style="danger">{saveError}</Text>}

                {canUpdate && (
                  <Actions>
                    <Button kind="primary" loading={loading} onClick={saveRolePermissions}>
                      Save role permissions
                    </Button>
                  </Actions>
                )}
              </>
            ) : (
              <EmptyState>Select a role to edit its permissions.</EmptyState>
            )}
          </Panel.Body>
        </Panel>
      </Layout>
    </>
  );
};

// Backward-compatible export for any external references.
export const RolePermissionsPanel = AccessControlPermissionsAdminRoles;
